/* Native details remain functional without JavaScript. This adds dismissal,
   keyboard navigation and clean Shopify editor section lifecycles only. */
(() => {
  if (window.effyShellInitialized) return;
  window.effyShellInitialized = true;

  const instances = new Map();

  function initialize(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const menus = Array.from(root.querySelectorAll('[data-effy-menu]'));
    const mobile = root.querySelector('[data-effy-mobile]');
    const summaryFor = details => details.querySelector(':scope > summary');
    const linksFor = menu => Array.from(menu.querySelectorAll('.effy-shell__dropdown a'));

    function closeMenus(except) {
      menus.forEach(menu => { if (menu !== except) menu.open = false; });
    }

    function closeMobile(restoreFocus = false) {
      if (!mobile || !mobile.open) return;
      mobile.open = false;
      closeMenus();
      if (restoreFocus) summaryFor(mobile).focus();
    }

    menus.forEach(menu => {
      const summary = summaryFor(menu);
      menu.addEventListener('toggle', () => {
        if (menu.open) closeMenus(menu);
      }, { signal });

      summary.addEventListener('keydown', event => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        event.stopPropagation();
        closeMenus(menu);
        menu.open = true;
        const links = linksFor(menu);
        (event.key === 'ArrowUp' ? links.at(-1) : links[0])?.focus();
      }, { signal });

      menu.addEventListener('keydown', event => {
        if (event.key === 'Escape' && menu.open) {
          event.preventDefault();
          event.stopPropagation();
          menu.open = false;
          summary.focus();
          return;
        }
        const links = linksFor(menu);
        const index = links.indexOf(document.activeElement);
        if (index < 0) return;
        let nextIndex;
        if (event.key === 'ArrowDown') nextIndex = (index + 1) % links.length;
        if (event.key === 'ArrowUp') nextIndex = (index - 1 + links.length) % links.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = links.length - 1;
        if (nextIndex !== undefined) {
          event.preventDefault();
          links[nextIndex].focus();
        }
      }, { signal });

      menu.addEventListener('focusout', event => {
        // Touch activation of a nested summary can clear focus without moving
        // it elsewhere. Only a known destination outside this menu dismisses it.
        if (event.relatedTarget instanceof Node && !menu.contains(event.relatedTarget)) {
          menu.open = false;
        }
      }, { signal });
    });

    root.addEventListener('keydown', event => {
      if (event.key === 'Escape' && mobile?.open) {
        event.preventDefault();
        closeMobile(true);
      }
    }, { signal });

    root.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('.effy-shell__mobile-panel a')) closeMobile();
    }, { signal });

    document.addEventListener('click', event => {
      if (!(event.target instanceof Node)) return;
      menus.forEach(menu => { if (!menu.contains(event.target)) menu.open = false; });
      if (mobile && !mobile.contains(event.target)) closeMobile();
    }, { signal });

    if (mobile) {
      mobile.addEventListener('toggle', () => {
        if (!mobile.open) closeMenus();
      }, { signal });
      mobile.addEventListener('focusout', event => {
        if (event.relatedTarget instanceof Node && !mobile.contains(event.relatedTarget)) {
          closeMobile();
        }
      }, { signal });
    }

    const breakpoint = window.matchMedia('(min-width: 1000px)');
    breakpoint.addEventListener('change', () => {
      const focusWasInside = root.contains(document.activeElement);
      closeMenus();
      closeMobile();
      if (focusWasInside) root.querySelector('.effy-shell__brand')?.focus();
    }, { signal });

    instances.set(root, controller);
  }

  function scan(container = document) {
    if (container.matches?.('[data-effy-header]')) initialize(container);
    container.querySelectorAll('[data-effy-header]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((controller, root) => {
      if (event.target === root || event.target.contains(root)) {
        controller.abort();
        instances.delete(root);
      }
    });
  });
})();

/* Cached Judge.me product names only: remove dated temporary title suffixes.
   Review text, review timestamps, links and the native EFFY shell stay intact. */
(() => {
  'use strict';
  if (window.effyJudgeMeProductTitleCleanerInitialized) return;
  window.effyJudgeMeProductTitleCleanerInitialized = true;
  const selector = '.jdgm-carousel-item__product-title';
  const reviewContent = '.jdgm-rev__body,.jdgm-carousel-item__review-body,.jdgm-rev__timestamp,.jdgm-carousel-item__timestamp';

  function cleanProductTitle(value) {
    const raw = String(value), pipe = raw.lastIndexOf('|');
    if (pipe < 0) return raw;
    const tail = raw.slice(pipe + 1).trim();
    if (!/^(?:pre[\s-]*order|restock|ships?)\b/i.test(tail)) return raw;
    const monthDate = /\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s*(?:(?:0?[1-9]|[12]\d|3[01])(?:st|nd|rd|th)?(?:\s*,?\s*20\d{2})?|20\d{2})(?!\d)/i;
    const isoDate = /\b20\d{2}[-/](?:0?[1-9]|1[0-2])[-/](?:0?[1-9]|[12]\d|3[01])\b/;
    const numericDate = /\b(?:(?:0?[1-9]|1[0-2])[/-](?:0?[1-9]|[12]\d|3[01])|(?:0?[1-9]|[12]\d|3[01])[/-](?:0?[1-9]|1[0-2]))[/-]20\d{2}\b/;
    if (!monthDate.test(tail) && !isoDate.test(tail) && !numericDate.test(tail)) return raw;
    const title = raw.slice(0, pipe).replace(/\s+$/, '');
    return title.trim() ? title : raw;
  }

  function cleanLabel(label) {
    if (!label.matches?.(selector) || label.closest(reviewContent) || label.querySelector(reviewContent)) return;
    const raw = label.textContent, clean = cleanProductTitle(raw);
    if (clean === raw) return;
    let remaining = clean.length;
    // Change text-node values only. Preserve anchors, hrefs and all child nodes.
    function keepPrefix(node) {
      if (node.nodeType === 3) {
        const keep = Math.max(0, Math.min(remaining, node.nodeValue.length));
        remaining -= keep;
        if (keep !== node.nodeValue.length) node.nodeValue = node.nodeValue.slice(0, keep);
      } else {
        Array.from(node.childNodes || []).forEach(keepPrefix);
      }
    }
    keepPrefix(label);
  }

  function collect(node, labels) {
    if (node.nodeType === 3) node = node.parentElement;
    if (!node || node.nodeType !== 1) return;
    const enclosing = node.closest?.(selector);
    if (enclosing) labels.add(enclosing);
    if (node.matches?.(selector)) labels.add(node);
    node.querySelectorAll?.(selector).forEach(label => labels.add(label));
  }

  function start() {
    document.querySelectorAll(selector).forEach(cleanLabel);
    const root = document.body || document.documentElement;
    if (!root) return;
    const observer = new MutationObserver(records => {
      const labels = new Set();
      records.forEach(record => {
        collect(record.target, labels);
        Array.from(record.addedNodes || []).forEach(node => collect(node, labels));
      });
      labels.forEach(cleanLabel);
    });
    observer.observe(root, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
